import { Close, EditOutlined, ErrorOutlined, SaveRounded, WarningOutlined } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Badge, Box, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from '@mui/material'
import { IContract } from '../../../../definitions/Contract'
import ContractsManager, { ValidationContract, ValidationError } from '../../../../managers/ContractsManager'

interface P {
    open: boolean
    onClose: () => void
    validationErrorList: ValidationContract
    onSave: (contract: IContract) => Promise<IContract>
    contract: IContract
    saveStatus: string
}

const ContractValidationModal = ({ open, onClose, onSave, validationErrorList, contract, saveStatus }: P) => {
    const handleSave = async () => {
        await onSave({ ...contract, lastUpdate: new Date().toISOString() })
        onClose()
    }

    const infoFieldError = (error: ValidationError, idx: number) => {
        const displayPath = ContractsManager.buildDisplayPath(error)

        return (
            <Box key={idx} display='flex' gap={1} alignItems='flex-start'>
                <ErrorOutlined sx={{ color: 'error.main', mt: 0.5 }} />
                <Box>
                    <Typography variant='body1' fontWeight='bold'>
                        {error.stepLabel}
                    </Typography>
                    <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                        {displayPath}
                    </Typography>
                    <Typography variant='body2' color='error.main' sx={{ mt: 0.5, fontStyle: 'italic' }}>
                        {error.message}
                    </Typography>
                </Box>
            </Box>
        )
    }

    const renderModalContent = () => {
        return (
            <>
                <DialogContent sx={{ pb: 2 }}>
                    <Box maxHeight='400px' display='flex' flexDirection='column'>
                        <Box display='flex' gap={1} alignItems='center'>
                            <WarningOutlined sx={{ color: 'warning.main' }} />
                            <Box>
                                <Typography variant='h6'>
                                    Unresolved errors - will not be sent to review
                                </Typography>
                                <Typography variant='subtitle2'>
                                    Complete these fields before submitting to review
                                </Typography>
                            </Box>
                        </Box>

                        {!validationErrorList?.isValid && (
                            <Box mt={2} display='flex' flexDirection='column' flex='1' minHeight={0}>
                                <Box display='flex' textAlign='center' alignItems='center' gap={2} mb={1}>
                                    <Typography variant='subtitle1'>Failed validations</Typography>
                                    <Badge
                                        variant='standard'
                                        badgeContent={validationErrorList?.errors.length}
                                        color='error'
                                    />
                                </Box>
                                <Stack spacing={2} sx={{ overflowY: 'auto' }} padding={1} flex={1} minHeight={0}>
                                    {validationErrorList?.errors.map((error, idx) =>
                                        infoFieldError(error, idx)
                                    )}
                                </Stack>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                    <LoadingButton
                        loading={saveStatus === 'loading'}
                        loadingPosition='start'
                        startIcon={<SaveRounded />}
                        variant='outlined'
                        onClick={handleSave}
                        sx={{ textTransform: 'none' }}>
                        SAVE ONLY
                    </LoadingButton>
                    <LoadingButton
                        startIcon={<EditOutlined />}
                        variant='contained'
                        loadingPosition='start'
                        onClick={() => onClose()}
                        sx={{ textTransform: 'none' }}>
                        Continue Editing
                    </LoadingButton>
                </DialogActions>
            </>
        )
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth='sm'
            fullWidth
            PaperProps={{ sx: { borderRadius: 2, minHeight: 450 } }}>
            <DialogTitle sx={{ pb: 1 }}>
                <Box display='flex' justifyContent='flex-end' alignItems='center'>
                    <IconButton onClick={onClose} size='small'>
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>
            {renderModalContent()}
        </Dialog>
    )
}

export default ContractValidationModal
